import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Send, Share2, Play, Pencil, Navigation } from 'lucide-react';

const MENU_WIDTH = 220;
const MENU_HEIGHT_ESTIMATE = 230;

export default function VehicleContextMenu({
  device,
  position,
  x,
  y,
  onClose,
  onSendCommand,
  onShareLink,
  onStartReplay,
  onEditDevice,
}) {
  const { t } = useTranslation();
  const menuRef = useRef(null);

  // Viewport-clamped position
  const clampedX = Math.min(x, window.innerWidth - MENU_WIDTH - 8);
  const clampedY = Math.min(y, window.innerHeight - MENU_HEIGHT_ESTIMATE - 8);

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  // Close on Escape
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') handleClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  // Close on scroll
  useEffect(() => {
    function onScroll() {
      handleClose();
    }
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [handleClose]);

  // Close on click outside
  useEffect(() => {
    function onPointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        handleClose();
      }
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [handleClose]);

  const hasPosition = position && position.latitude != null && position.longitude != null;
  const googleMapsUrl = hasPosition
    ? `https://www.google.com/maps/dir/?api=1&destination=${position.latitude},${position.longitude}`
    : null;

  // An action whose handler the caller did not pass is not offered (the
  // enterprise Live Map is read-only and passes none of them).
  const actions = [
    onSendCommand && {
      icon: Send,
      label: t('contextMenu.sendCommand', 'Send Command'),
      onClick: () => { onSendCommand?.(device); handleClose(); },
    },
    onShareLink && {
      icon: Share2,
      label: t('contextMenu.shareLink', 'Share Link'),
      onClick: () => { onShareLink?.(device); handleClose(); },
    },
    onStartReplay && {
      icon: Play,
      label: t('contextMenu.startReplay', 'Start Replay'),
      onClick: () => { onStartReplay?.(device); handleClose(); },
    },
    onEditDevice && {
      icon: Pencil,
      label: t('contextMenu.editDevice', 'Edit Device'),
      onClick: () => { onEditDevice?.(device); handleClose(); },
    },
    {
      icon: Navigation,
      label: t('contextMenu.navigateTo', 'Navigate To'),
      disabled: !hasPosition,
      onClick: () => {
        if (googleMapsUrl) window.open(googleMapsUrl, '_blank');
        handleClose();
      },
    },
  ].filter(Boolean);

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[220px] bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-xl shadow-xl dark:shadow-black/50 border border-slate-200/60 dark:border-slate-700/60 py-1 overflow-hidden"
      style={{ left: clampedX, top: clampedY }}
    >
      {/* Device name header */}
      <div className="px-3 py-2 border-b border-slate-200/60 dark:border-slate-700/60">
        <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate text-start">
          <bdi>{device?.name || '—'}</bdi>
        </p>
      </div>

      {/* Actions */}
      <div className="py-1">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            disabled={action.disabled}
            onClick={action.onClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-start text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <action.icon size={15} className="flex-shrink-0 text-slate-500 dark:text-slate-400" />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}
